import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ApolloError } from "@apollo/client";
import { CheckCircle2, Circle, ChevronLeft, ChevronRight, Search, Building2, MapPin, Plus } from "lucide-react";
import { Button, Modal, message } from "antd";
import { nanoid } from "nanoid";

import OllieLayout from "components/layouts/ollie-layout/ollie-layout";
import OllieInspectionGate from "components/ollie-inspection-gate/ollie-inspection-gate";
import PropertyForm, { PropertyFormOutput, uploadPropertyFormData } from "components/property-form/property-form";
import InviteOrgModal, { NewOrgData, TEMP_ORG_PREFIX } from "components/invite-org-modal/invite-org-modal";
import LoadingOverlay from "components/loading-overlay/loading-overlay";
import TestingGuide from "components/testing-guide/testing-guide";
import JurisdictionAutoNote from "components/jurisdiction-auto-note/jurisdiction-auto-note";

import { useGetOrganizationsFilteredQuery } from "graphql/queries/get-organizations-filtered.generated";
import { useCreateInspectionJobMutation } from "graphql/mutations/create-inspection-job.generated";
import {
	CreateOrganizationInput,
	CreatePropertyInput,
	InputMaybe,
	StartInspectionJobInput,
	UserInvitationInput
} from "graphql/generated/graphql";
import { useGetPropertiesByOrganizationIdQuery } from "graphql/queries/get-properties-by-organization-id.generated";
import { useGetJurisdictionsQuery } from "graphql/queries/get-jurisdictions.generated";
import { useGetInspectionJobSubscription } from "graphql/subscriptions/inspection-job-by-id.generated";
import { useUpdateInspectionJobStateMutation } from "graphql/mutations/update-inspection-job-state.generated";
import { getPropertyAddress } from "utils/helpers";

import { useResolvedJurisdiction } from "hooks/useResolvedJurisdiction";
import { useLoggedUser } from "state/reactive-vars/logged-user-var";

import routeNames from "constants/route-names";
import {
	OrganizationType,
	InspectionJobStatus,
	isAuthorityRole,
	isCustomerRole,
	isPlatformAdminRole,
	isOllieInspectionsGatedRole
} from "constants/field-values";
import { INSPECTION_TYPE_OPTIONS } from "constants/constants";
import { useDescribeScreenToOllie } from "state/reactive-vars/ollie-screen-description-var";
import { buildScreenDescription } from "utils/ollie-screen-description";

const TEMP_PROPERTY_PREFIX = "__temp_property__";

export enum FlowStage {
	SELECT_ORGANIZATION = "select-organization",
	INSPECTION_SETUP = "inspection-setup"
}

// The actual inspection setup flow. Split out from OllieInspectionAdd below so a
// gated role never mounts it — the queries it fires on mount (organizations,
// properties, jurisdictions) would otherwise run for someone who cannot get past
// the paywall anyway.
const OllieInspectionAddForm = () => {
	// Tells Ollie what the reader is looking at.
	useDescribeScreenToOllie(
		buildScreenDescription({
			screen:
				"The screen for starting a new inspection. The reader is choosing the property, the authority having jurisdiction and the inspection type; the checklist is generated from those."
		})
	);

	const navigate = useNavigate();
	const { loggedUserValue } = useLoggedUser();

	const baseUrl = typeof window !== "undefined" ? window.location.origin : "";

	const [flowStage, setFlowStage] = useState<FlowStage>(FlowStage.SELECT_ORGANIZATION);
	const [selectedPropertyData, setSelectedPropertyData] = useState({
		id: "",
		name: "",
		address: ""
	});
	// Fixed temp ID to identify an unsaved new property across re-renders
	const [newPropertyTempId] = useState<string>(`${TEMP_PROPERTY_PREFIX}${nanoid()}`);
	const [propertySearchQuery, setPropertySearchQuery] = useState("");
	// An authority user only ever inspects for its own jurisdiction, so theirs is the
	// answer and there is nothing to look up.
	const lockedJurisdictionId = isAuthorityRole(loggedUserValue.role)
		? loggedUserValue.organization?.jurisdiction_id || undefined
		: undefined;
	const [jurisdictionSearchQuery, setJurisdictionSearchQuery] = useState("");
	const [inspectionType, setInspectionType] = useState("");
	const [inspectionTypeSearchQuery, setInspectionTypeSearchQuery] = useState("");
	const [isPropertyDropdownOpen, setIsPropertyDropdownOpen] = useState(false);
	const [isInspectionTypeDropdownOpen, setIsInspectionTypeDropdownOpen] = useState(false);
	const [isAhjDropdownOpen, setIsAhjDropdownOpen] = useState(false);
	const [isCreatePropertyModalOpen, setIsCreatePropertyModalOpen] = useState(false);
	const [newPropertyFormOutput, setNewPropertyFormOutput] = useState<PropertyFormOutput | null>(null);

	const [isInviteOrgModalOpen, setIsInviteOrgModalOpen] = useState(false);

	const [newOrgData, setNewOrgData] = useState<NewOrgData | null>(null);
	const [newUserData, setNewUserData] = useState<{ email: string } | null>(null);
	const [selectedOrganizationData, setSelectedOrganizationData] = useState({
		id: "",
		name: ""
	});
	const [pendingJobId, setPendingJobId] = useState<string | null>(null);
	const [isGeneratingChecklist, setIsGeneratingChecklist] = useState(false);

	// A customer user inspects only its own properties, so its own organization is the
	// only one it can pick and it cannot bring in new ones.
	const isCustomerUser = isCustomerRole(loggedUserValue.role);
	// The AHJ still fills itself in for everybody. What only the platform admin sees is
	// the commentary about how it got there: for anybody else it is noise about internals
	// they cannot act on, since loading the maps is not part of their job.
	const canSeeJurisdictionInternals = isPlatformAdminRole(loggedUserValue.role);

	const organizationsFilter = useMemo(
		() =>
			isCustomerUser
				? { id: { _eq: loggedUserValue.organization_id } }
				: { workspace_id: { _eq: loggedUserValue.workspace_id } },
		[isCustomerUser, loggedUserValue.organization_id, loggedUserValue.workspace_id]
	);

	const { data: organizationsData, loading: loadingOrganizations } = useGetOrganizationsFilteredQuery({
		// Hasura drops a null _eq from the where clause, which would list every organization
		skip: isCustomerUser && !loggedUserValue.organization_id,
		variables: { where: organizationsFilter },
		errorPolicy: "none",
		fetchPolicy: "network-only",
		nextFetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onError: useCallback((err: ApolloError) => {
			console.error("getOrganizationsFiltered error:", err);
		}, [])
	});

	const orgOptions = useMemo(() => {
		const { organizations = [] } = organizationsData || {};

		return [
			...organizations,
			...(newOrgData
				? [
						{
							id: newOrgData.tempId,
							name: newOrgData.name,
							email: newOrgData.email,
							organizationImageURL: "",
							org_properties: []
						}
					]
				: [])
		];
	}, [organizationsData, newOrgData]);

	const handleOnErrorGetPropertiesByOrganizationId = useCallback((error: ApolloError) => {
		console.log("GetPropertiesByOrganizationId Error: ", error);
	}, []);

	const handleOnCompleteGetPropertiesByOrganizationId = useCallback(data => {
		//console.log("GetPropertiesByOrganizationId Complete data: ", data);
	}, []);

	const { data: propertiesByOrganizationData, loading: loadingPropertiesByOrganization } =
		useGetPropertiesByOrganizationIdQuery({
			skip: !selectedOrganizationData.id || selectedOrganizationData.id?.startsWith(TEMP_ORG_PREFIX),
			variables: {
				organization_id: selectedOrganizationData.id
			},
			errorPolicy: "none",
			fetchPolicy: "cache-first",
			nextFetchPolicy: "network-only",
			notifyOnNetworkStatusChange: true,
			onError: handleOnErrorGetPropertiesByOrganizationId,
			onCompleted: handleOnCompleteGetPropertiesByOrganizationId
		});

	const propertiesOptions = useMemo(() => {
		const { properties = [] } = propertiesByOrganizationData || {};

		// Include the staged new property as a selectable option once the user has saved the form
		const newPropertyOption = newPropertyFormOutput
			? [{ id: newPropertyTempId, name: newPropertyFormOutput.name, address: newPropertyFormOutput.address || "" }]
			: [];

		return [
			...properties.map(p => ({
				...p,
				address: getPropertyAddress(p.address, p.standardized_address)
			})),
			...newPropertyOption
		];
	}, [propertiesByOrganizationData, newPropertyFormOutput, newPropertyTempId]);

	const { data: jurisdictionsData, loading: loadingJurisdictions } = useGetJurisdictionsQuery({
		errorPolicy: "none",
		fetchPolicy: "cache-first",
		notifyOnNetworkStatusChange: true
	});

	// Group jurisdictions by county for <optgroup> sections
	const jurisdictionsGrouped = useMemo(() => {
		const jurisdictions = jurisdictionsData?.jurisdictions ?? [];
		return jurisdictions.reduce<Record<string, typeof jurisdictions>>((acc, j) => {
			const key = j.county ?? j.state;
			if (!acc[key]) acc[key] = [];
			acc[key].push(j);
			return acc;
		}, {});
	}, [jurisdictionsData]);

	const isStagedProperty = !!newPropertyFormOutput && selectedPropertyData.id === newPropertyTempId;

	// A staged property has no row to look up yet, so the lookup works from the
	// address the form collected. The autocomplete gives structured components when
	// the user picked a suggestion; country is implied by the US-only address search.
	const stagedStandardizedAddress = useMemo(() => {
		const addressComponents = newPropertyFormOutput?.selectedAddressComponents;
		if (!isStagedProperty || !addressComponents) return undefined;

		return {
			street_1: addressComponents.street_1,
			street_2: addressComponents.street_2 || null,
			city: addressComponents.city,
			state: addressComponents.state,
			postal_code: addressComponents.postal_code,
			country: "US"
		};
	}, [isStagedProperty, newPropertyFormOutput]);

	// The AHJ comes from where the property is and what kind of inspection it is, so
	// the inspector does not have to know which office enforces the code at that
	// address. The dropdown below stays for the cases the lookup cannot settle.
	const {
		jurisdictionId,
		selectJurisdiction,
		applySuggestion,
		suggestion: jurisdictionSuggestion,
		loading: resolvingJurisdiction,
		isAutoSelected: isJurisdictionAutoSelected,
		isLocked: isJurisdictionLocked
	} = useResolvedJurisdiction({
		propertyId: isStagedProperty ? undefined : selectedPropertyData.id || undefined,
		address: isStagedProperty ? newPropertyFormOutput?.address : undefined,
		standardizedAddress: stagedStandardizedAddress,
		inspectionType: inspectionType || undefined,
		lockedJurisdictionId,
		// The AHJ depends on the kind of inspection, and the list of kinds is fixed and
		// on screen. Asking before one is picked would answer against a default and
		// then correct itself, which reads as the system changing its mind.
		enabled: !!inspectionType
	});

	const handleOnErrorCreateInspectionJobMutation = useCallback((error: ApolloError) => {
		console.log("createInspectionJobMutation Error: ", error);
		message.error("Ollie couldn't create the inspection. Please try again.");
	}, []);

	const handleOnCompleteCreateInspectionJobMutation = useCallback(data => {
		console.log("createInspectionJobMutation Complete data: ", data);
		const result = data?.start_inspection_job;

		if (result?.success && result?.job_id) {
			// The job exists but its checklist is still generating in the background.
			// Don't navigate yet — subscribe to the job and wait for COLLECTING (navigate) or FAILED (offer retry).
			setPendingJobId(result.job_id);
			setIsGeneratingChecklist(true);
			return;
		}

		// The action itself returned success:false (failed before the job was created).
		setIsGeneratingChecklist(false);
		message.error(result?.error || "Ollie couldn't create the inspection. Please try again.");
	}, []);

	const [createInspectionJobMutation, { data, loading, error }] = useCreateInspectionJobMutation({
		errorPolicy: "none",
		fetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onError: handleOnErrorCreateInspectionJobMutation,
		onCompleted: handleOnCompleteCreateInspectionJobMutation
	});

	const [updateInspectionJobStateMutation] = useUpdateInspectionJobStateMutation();

	// Watch the pending job while its checklist generates in the background.
	// COLLECTING → checklist ready, navigate to evidence collection.
	// FAILED → generation failed, stop the overlay and surface a retryable message.
	useGetInspectionJobSubscription({
		skip: !pendingJobId,
		variables: { id: pendingJobId as string },
		onData: ({ data: subData }) => {
			const job = subData.data?.inspection_jobs_by_pk;
			if (!job) return;

			if (job.state === InspectionJobStatus.COLLECTING) {
				setIsGeneratingChecklist(false);
				navigate(routeNames.OLLIE_EVIDENCE_COLLECTION.replace(":jobId", job.id));
			} else if (job.state === InspectionJobStatus.FAILED) {
				setIsGeneratingChecklist(false);
				message.error(job.error_message || "Ollie couldn't generate the inspection checklist. Please try again.");
			}
		}
	});

	const selectedInspectionTypeLabel = INSPECTION_TYPE_OPTIONS.find(o => o.value === inspectionType)?.label ?? "";

	const filteredInspectionTypes = INSPECTION_TYPE_OPTIONS.filter(o =>
		o.label.toLowerCase().includes(inspectionTypeSearchQuery.toLowerCase())
	);

	const filteredProperties = propertiesOptions.filter(
		p =>
			p.name.toLowerCase().includes(propertySearchQuery.toLowerCase()) ||
			p.address?.toLowerCase().includes(propertySearchQuery.toLowerCase())
	);

	// Flat list of all jurisdictions for filtering
	const allJurisdictions = useMemo(() => jurisdictionsData?.jurisdictions ?? [], [jurisdictionsData]);

	const selectedJurisdictionLabel = useMemo(
		() => allJurisdictions.find(j => j.id === jurisdictionId)?.name ?? "",
		[allJurisdictions, jurisdictionId]
	);

	const filteredJurisdictions = useMemo(() => {
		if (!jurisdictionSearchQuery) return jurisdictionsGrouped;
		const q = jurisdictionSearchQuery.toLowerCase();
		const result: typeof jurisdictionsGrouped = {};
		Object.entries(jurisdictionsGrouped).forEach(([county, items]) => {
			const matched = items.filter(j => j.name.toLowerCase().includes(q) || county.toLowerCase().includes(q));
			if (matched.length > 0) result[county] = matched;
		});
		return result;
	}, [jurisdictionsGrouped, jurisdictionSearchQuery]);

	const handleOnSaveNewOrganization = useCallback(args => {
		const { tempId, orgName, orgEmail, userEmail } = args;
		setNewOrgData({ tempId, name: orgName, email: orgEmail });
		setSelectedOrganizationData({
			id: tempId,
			name: orgName
		});
		setNewUserData(userEmail.trim() ? { email: userEmail.trim() } : null);
		setIsInviteOrgModalOpen(false);
		setSelectedPropertyData({ id: "", name: "", address: "" });
		setPropertySearchQuery("");
		setNewPropertyFormOutput(null);
	}, []);

	const handleOnSelectOrganization = useCallback(
		orgValues => {
			if (selectedOrganizationData.id === orgValues.id) return; // No change, do nothing
			setSelectedOrganizationData(orgValues);
			setSelectedPropertyData({ id: "", name: "", address: "" });
			setPropertySearchQuery("");
			setNewPropertyFormOutput(null);
		},
		[selectedOrganizationData.id]
	);

	// Saves the new property form output and updates selectedPropertyData so the
	// search input and canStart check reflect the staged property before it is persisted
	const handleNewPropertySave = useCallback(
		(output: PropertyFormOutput) => {
			setNewPropertyFormOutput(output);
			setSelectedPropertyData({
				id: newPropertyTempId,
				name: output.name,
				address: output.address || ""
			});
			setPropertySearchQuery(output.name);
			setIsCreatePropertyModalOpen(false);
		},
		[newPropertyTempId]
	);

	// Closes the property modal; if the user never saved, clears the temp selection
	const handleAddPropertyCancel = useCallback(() => {
		setIsCreatePropertyModalOpen(false);
		if (selectedPropertyData.id === newPropertyTempId && !newPropertyFormOutput) {
			setSelectedPropertyData({ id: "", name: "", address: "" });
			setPropertySearchQuery("");
		}
	}, [selectedPropertyData.id, newPropertyTempId, newPropertyFormOutput]);

	// Discards the staged new property and resets the search input
	const handleRemoveNewProperty = useCallback(() => {
		setNewPropertyFormOutput(null);
		setSelectedPropertyData({ id: "", name: "", address: "" });
		setPropertySearchQuery("");
	}, []);

	const handleStartInspection = useCallback(async () => {
		// Retry path: a job already exists but its checklist generation failed.
		// Re-run generation on the SAME job (set it back to PENDING_CHECKLIST, which
		// re-triggers the backend) instead of creating a duplicate org/property/job.
		if (pendingJobId && !isGeneratingChecklist) {
			setIsGeneratingChecklist(true);
			updateInspectionJobStateMutation({
				variables: { id: pendingJobId, state: InspectionJobStatus.PENDING_CHECKLIST }
			}).catch(() => {
				setIsGeneratingChecklist(false);
				message.error("Ollie couldn't create the inspection. Please try again.");
			});
			return;
		}

		let new_organization: InputMaybe<CreateOrganizationInput> = null;
		let new_property: InputMaybe<CreatePropertyInput> = null;
		let new_user: InputMaybe<UserInvitationInput> = null;

		if (newOrgData) {
			new_organization = {
				name: newOrgData.name,
				email: newOrgData.email,
				type: OrganizationType.Customer,
				workspace_id: loggedUserValue?.workspace_id,
				created_by_user_id: loggedUserValue?.id,
				display_price: false,
				organizationImageURL: null
			};
		}

		if (newUserData) {
			new_user = {
				email: newUserData.email,
				workspace_id: loggedUserValue?.workspace_id
			};
		}

		// If the selected property is the staged new property, upload its form data so the
		// backend receives a CreatePropertyInput and can persist it during the same transaction
		const isNewProperty = selectedPropertyData.id === newPropertyTempId;
		if (isNewProperty && newPropertyFormOutput) {
			new_property = await uploadPropertyFormData(newPropertyFormOutput, loggedUserValue);
		}

		const inspectionInput: StartInspectionJobInput = {
			// Pass null for new properties — the backend assigns a real ID after creation
			property_id: isNewProperty ? null : selectedPropertyData.id,
			inspection_type: inspectionType,
			jurisdiction_id: jurisdictionId,
			created_by_user_id: loggedUserValue?.id,
			base_url: baseUrl,
			workspace_id: loggedUserValue?.workspace_id,
			organization_id: selectedOrganizationData.id,
			new_organization,
			new_property,
			new_user
		};

		createInspectionJobMutation({ variables: { input: inspectionInput } });
	}, [
		pendingJobId,
		isGeneratingChecklist,
		updateInspectionJobStateMutation,
		selectedOrganizationData.id,
		newOrgData,
		newUserData,
		loggedUserValue,
		baseUrl,
		inspectionType,
		jurisdictionId,
		selectedPropertyData,
		newPropertyTempId,
		newPropertyFormOutput,
		createInspectionJobMutation
	]);

	// If the user edits the setup after a failed attempt, drop the stale job so the
	// next Start creates a fresh one instead of retrying the previous job. Guarded by
	// !isGeneratingChecklist so an in-flight generation is never cleared.
	useEffect(() => {
		if (pendingJobId && !isGeneratingChecklist) {
			setPendingJobId(null);
		}
	}, [selectedPropertyData.id, jurisdictionId, inspectionType, selectedOrganizationData.id]);

	const canStart =
		!!selectedPropertyData.name && !!selectedPropertyData.address && !!inspectionType && !!jurisdictionId;

	const isLoading =
		loading || isGeneratingChecklist || loadingOrganizations || loadingPropertiesByOrganization || loadingJurisdictions;

	return (
		<>
			<OllieLayout>
				<div className="iia-setup-page">
					<Link to={routeNames.OLLIE_DASHBOARD_INSPECTIONS} className="iia-back-btn">
						<ChevronLeft className="iia-back-icon" />
						Dashboard
					</Link>

					{flowStage === FlowStage.INSPECTION_SETUP && (
						<div className="iia-setup-content iia-animate">
							<div className="iia-select-org-card">
								<div className="iia-modal-hero">
									<div
										className="iia-select-org-hero-img"
										style={{
											backgroundImage: `url(${process.env.PUBLIC_URL}/assets/images/layout/ollie-forms-hero.png)`
										}}
									/>
									<div className="iia-modal-hero-avatar-wrap">
										<div className="iia-card-hero-avatar-inner">
											<div className="iia-ollie-avatar iia-ollie-avatar-xl">
												<img src={`${process.env.PUBLIC_URL}/assets/images/icons/ollie-owl.svg`} alt="Ollie AI" />
											</div>
										</div>
									</div>
								</div>

								<div className="iia-modal-content">
									<div className="iia-modal-header">
										<h2 className="iia-modal-title">Inspection Setup</h2>
										<p className="iia-modal-subtitle">Tell me about the property we're inspecting today.</p>

										{canSeeJurisdictionInternals && (
											<TestingGuide
												title="What this screen does for you now"
												summary={`The AHJ field used to be an empty dropdown you had to already know the answer for. Now it fills itself in from the property's address and the kind of inspection you picked, so nobody has to look up which office reviews a given building.`}
												steps={[
													<>Pick an organization, then a property with a real address.</>,
													<>
														Pick an <strong>Inspection Type</strong>. Nothing is looked up until you do — the kind of
														inspection is half the question, so we wait for it instead of guessing and correcting
														ourselves a second later.
													</>,
													<>
														Watch the <strong>AHJ</strong> field fill itself in, with a line underneath saying how it got
														there.
													</>,
													<>
														Change the inspection type. The AHJ can change with it: some counties publish a different
														office for construction work than for the recurring inspections of a building in use, and we
														follow that.
													</>,
													<>
														Type over the AHJ and pick a different one. The line changes to tell you what we would have
														said, with a button to go back to it. What you pick is what gets filed.
													</>
												]}
												notMistakes={[
													<>
														<strong>An amber &quot;please check this is right&quot; on every AHJ.</strong> It means the
														answer came from matching the city name in the address, not from a map of the area. A city
														name cannot tell an address inside the city limits from one just outside it, so we always ask.
														Load that region&apos;s map in <strong>Jurisdiction Maps</strong> and the same addresses stop
														asking.
													</>,
													<>
														<strong>Your pick is not remembered next time.</strong> On purpose. Every lookup is worked out
														again from the address and the map, both of which anyone can go and check. A remembered choice
														cannot be checked against anything, so one wrong pick would quietly become that property&apos;s
														permanent answer. What you filed under is kept on the inspection itself.
													</>,
													<>
														<strong>Creating the inspection fails afterwards.</strong> That is the checklist step, which is
														a different service. The AHJ is filled in before you submit, so this screen can be judged
														without pressing the last button.
													</>
												]}
											/>
										)}
									</div>

									<div className="iia-card-fields">
										<div className="iia-field-group iia-field-property">
											<label className="iia-field-label">Organization</label>
											<div className="iia-selected-organization-container">
												<span className="iia-selected-organization-name">{selectedOrganizationData.name}</span>
												<button
													type="button"
													className="ha-new-org-tag-btn"
													onClick={() => setFlowStage(FlowStage.SELECT_ORGANIZATION)}
													title="Edit"
												>
													<img src={process.env.PUBLIC_URL + "/assets/images/icons/edit-pencil.svg"} alt="Edit" />
												</button>
											</div>
										</div>

										<div
											className={`iia-field-group iia-field-property ${isPropertyDropdownOpen ? "iia-field-active" : ""}`}
										>
											<label className="iia-field-label">Property</label>
											<div className="iia-search-wrapper">
												<Search className="iia-search-prefix-icon" />
												<input
													className="iia-search-input"
													placeholder="Search by name or address..."
													value={propertySearchQuery}
													onChange={e => {
														setPropertySearchQuery(e.target.value);
														setIsPropertyDropdownOpen(true);
														if (selectedPropertyData.name && e.target.value !== selectedPropertyData.name) {
															setSelectedPropertyData({ ...selectedPropertyData, name: "", address: "" });
														}
													}}
													onFocus={() => setIsPropertyDropdownOpen(true)}
													onBlur={() => setTimeout(() => setIsPropertyDropdownOpen(false), 200)}
												/>
												{selectedPropertyData.name && <CheckCircle2 className="iia-search-check-icon" />}
											</div>

											{isPropertyDropdownOpen && (
												<div className="iia-dropdown">
													{filteredProperties.length > 0 ? (
														<div className="iia-dropdown-list">
															{filteredProperties.map(prop => (
																<button
																	key={prop.id}
																	className="iia-dropdown-item"
																	onClick={() => {
																		setSelectedPropertyData({
																			...prop,
																			name: prop.name,
																			address: prop.address as string
																		});
																		setPropertySearchQuery(prop.name);
																		setIsPropertyDropdownOpen(false);
																	}}
																	type="button"
																>
																	<div className="iia-dropdown-item-icon">
																		<Building2 className="iia-dropdown-item-icon-svg" />
																	</div>
																	<div>
																		<p className="iia-dropdown-item-name">{prop.name}</p>
																		<p className="iia-dropdown-item-address">
																			<MapPin className="iia-dropdown-item-pin" />
																			<span>{prop.address}</span>
																		</p>
																	</div>
																</button>
															))}
															<div className="iia-dropdown-create-row">
																<Button
																	type="text"
																	className="iia-dropdown-create-btn"
																	icon={<Plus />}
																	onClick={e => {
																		e.preventDefault();
																		e.stopPropagation();
																		setIsPropertyDropdownOpen(false);
																		setIsCreatePropertyModalOpen(true);
																	}}
																>
																	Create new property
																</Button>
															</div>
														</div>
													) : (
														<div className="iia-dropdown-empty">
															<p className="iia-dropdown-empty-text">
																No properties found matching &quot;{propertySearchQuery}&quot;
															</p>
															<Button
																className="iia-dropdown-create-outline-btn"
																icon={<Plus />}
																onClick={e => {
																	e.preventDefault();
																	e.stopPropagation();
																	setIsPropertyDropdownOpen(false);
																	setIsCreatePropertyModalOpen(true);
																}}
															>
																Create new property
															</Button>
														</div>
													)}
												</div>
											)}
										</div>

										<div className="iia-field-grid">
											{/* Inspection Type — custom searchable dropdown */}
											<div
												className={`iia-field-group iia-field-property ${isInspectionTypeDropdownOpen ? "iia-field-active" : ""}`}
											>
												<label className="iia-field-label">Inspection Type</label>
												<div className="iia-search-wrapper">
													<Search className="iia-search-prefix-icon" />
													<input
														className="iia-search-input"
														placeholder="Select type..."
														value={
															isInspectionTypeDropdownOpen ? inspectionTypeSearchQuery : selectedInspectionTypeLabel
														}
														onChange={e => {
															setInspectionTypeSearchQuery(e.target.value);
															setIsInspectionTypeDropdownOpen(true);
															if (inspectionType && e.target.value !== selectedInspectionTypeLabel) {
																setInspectionType("");
															}
														}}
														onFocus={() => {
															setInspectionTypeSearchQuery("");
															setIsInspectionTypeDropdownOpen(true);
														}}
														onBlur={() => setTimeout(() => setIsInspectionTypeDropdownOpen(false), 200)}
														readOnly={!isInspectionTypeDropdownOpen && !!inspectionType}
													/>
													{inspectionType && <CheckCircle2 className="iia-search-check-icon" />}
												</div>

												{isInspectionTypeDropdownOpen && (
													<div className="iia-dropdown">
														{filteredInspectionTypes.length > 0 ? (
															<div className="iia-dropdown-list">
																{filteredInspectionTypes.map(opt => (
																	<button
																		key={opt.value}
																		className={`iia-dropdown-item iia-dropdown-item-simple${inspectionType === opt.value ? " iia-dropdown-item-active" : ""}`}
																		onClick={() => {
																			setInspectionType(opt.value);
																			setInspectionTypeSearchQuery("");
																			setIsInspectionTypeDropdownOpen(false);
																		}}
																		type="button"
																	>
																		<span className="iia-dropdown-item-name">{opt.label}</span>
																	</button>
																))}
															</div>
														) : (
															<div className="iia-dropdown-empty">
																<p className="iia-dropdown-empty-text">
																	No results for &quot;{inspectionTypeSearchQuery}&quot;
																</p>
															</div>
														)}
													</div>
												)}
											</div>

											{/* AHJ — custom searchable dropdown grouped by county */}
											<div
												className={`iia-field-group iia-field-property ${isAhjDropdownOpen ? "iia-field-active" : ""}`}
											>
												<label className="iia-field-label">AHJ</label>
												<div className="iia-search-wrapper">
													<Search className="iia-search-prefix-icon" />
													<input
														className="iia-search-input"
														placeholder={
															loadingJurisdictions || resolvingJurisdiction ? "Loading…" : "Search jurisdiction..."
														}
														disabled={loadingJurisdictions || isJurisdictionLocked}
														value={isAhjDropdownOpen ? jurisdictionSearchQuery : selectedJurisdictionLabel}
														onChange={e => {
															setJurisdictionSearchQuery(e.target.value);
															setIsAhjDropdownOpen(true);
															// Typing over the value is an override in progress: drop what
															// was selected so the lookup does not put it straight back.
															if (jurisdictionId && e.target.value !== selectedJurisdictionLabel) {
																selectJurisdiction("");
															}
														}}
														onFocus={() => {
															setJurisdictionSearchQuery("");
															setIsAhjDropdownOpen(true);
														}}
														onBlur={() => setTimeout(() => setIsAhjDropdownOpen(false), 200)}
														readOnly={!isAhjDropdownOpen && !!jurisdictionId}
													/>
													{jurisdictionId && <CheckCircle2 className="iia-search-check-icon" />}
												</div>

												{isAhjDropdownOpen && (
													<div className="iia-dropdown">
														{Object.keys(filteredJurisdictions).length > 0 ? (
															<div className="iia-dropdown-list">
																{Object.entries(filteredJurisdictions).map(([county, items]) => (
																	<div key={county}>
																		<div className="iia-dropdown-group-label">{county}</div>
																		{items.map(j => (
																			<button
																				key={j.id}
																				className={`iia-dropdown-item iia-dropdown-item-simple${jurisdictionId === j.id ? " iia-dropdown-item-active" : ""}`}
																				onClick={() => {
																					selectJurisdiction(j.id);
																					setJurisdictionSearchQuery("");
																					setIsAhjDropdownOpen(false);
																				}}
																				type="button"
																			>
																				<span className="iia-dropdown-item-name">{j.name}</span>
																			</button>
																		))}
																	</div>
																))}
															</div>
														) : (
															<div className="iia-dropdown-empty">
																<p className="iia-dropdown-empty-text">
																	No jurisdictions found for &quot;{jurisdictionSearchQuery}&quot;
																</p>
															</div>
														)}
													</div>
												)}

												{!isJurisdictionLocked && canSeeJurisdictionInternals && (
													<JurisdictionAutoNote
														suggestion={jurisdictionSuggestion}
														loading={resolvingJurisdiction}
														isAutoSelected={isJurisdictionAutoSelected}
														onApplySuggestion={applySuggestion}
													/>
												)}
											</div>
										</div>

										{/* New property summary — shown when the user has staged a new property to be created */}
										{newPropertyFormOutput && selectedPropertyData.id === newPropertyTempId && (
											<div className="ha-field-group org">
												<label className="iia-field-label">New Property</label>
												<div className="ha-org-summary">
													<div className="ha-org-summary-body">
														<div className="ha-org-summary-section">
															<div className="ha-summary-row">
																<span className="ha-summary-key">Property name:</span>
																<span className="ha-summary-value">
																	{newPropertyFormOutput.name}
																	<span className="ha-new-org-tag-badge">New</span>
																</span>
															</div>
															{newPropertyFormOutput.address && (
																<div className="ha-summary-row">
																	<span className="ha-summary-key">Address:</span>
																	<span className="ha-summary-value">{newPropertyFormOutput.address}</span>
																</div>
															)}
														</div>
													</div>
													{/* Edit / remove actions for the staged new property */}
													<div className="ha-new-org-tag-actions ha-org-summary-actions">
														<button
															type="button"
															className="ha-new-org-tag-btn"
															onClick={() => setIsCreatePropertyModalOpen(true)}
															title="Edit"
														>
															<img src={process.env.PUBLIC_URL + "/assets/images/icons/edit-pencil.svg"} alt="Edit" />
														</button>
														<button
															type="button"
															className="ha-new-org-tag-btn"
															onClick={handleRemoveNewProperty}
															title="Remove"
														>
															<img
																src={process.env.PUBLIC_URL + "/assets/images/icons/remove-trash.svg"}
																alt="Remove"
															/>
														</button>
													</div>
												</div>
											</div>
										)}

										<button
											className={`iia-start-btn ${!canStart || isGeneratingChecklist ? "iia-start-btn-disabled" : ""}`}
											onClick={handleStartInspection}
											disabled={!canStart || isGeneratingChecklist}
										>
											{pendingJobId && !isGeneratingChecklist ? "Retry" : "Start"}
											<ChevronRight className="iia-start-btn-icon" />
										</button>
									</div>
								</div>
							</div>
						</div>
					)}

					{flowStage === FlowStage.SELECT_ORGANIZATION && (
						<div className="iia-setup-content iia-animate">
							<div className="iia-select-org-card">
								<div className="iia-modal-hero">
									<div
										className="iia-select-org-hero-img"
										style={{
											backgroundImage: `url(${process.env.PUBLIC_URL}/assets/images/layout/ollie-forms-hero.png)`
										}}
									/>
									<div className="iia-modal-hero-avatar-wrap">
										<div className="iia-card-hero-avatar-inner">
											<div className="iia-ollie-avatar iia-ollie-avatar-xl">
												<img src={`${process.env.PUBLIC_URL}/assets/images/icons/ollie-owl.svg`} alt="Ollie AI" />
											</div>
										</div>
									</div>
								</div>

								<div className="iia-modal-content">
									<div className="iia-modal-header">
										<h2 className="iia-modal-title">Select Organization</h2>
										<p className="iia-modal-subtitle">Which customer does this property belong to?</p>
									</div>

									<div className="iia-org-list">
										{orgOptions.map(org => {
											const { id = "", name = "", organizationImageURL = "", org_properties = [] } = org || {};

											return (
												<div
													key={id}
													className={`iia-org-card ${selectedOrganizationData.id === id ? "selected" : ""}`}
													onClick={() => handleOnSelectOrganization({ id, name })}
												>
													<div className="iia-org-icon">
														{organizationImageURL ? (
															<img src={organizationImageURL} alt={`Organization Icon - ${name}`} />
														) : (
															<Building2 size={20} />
														)}
													</div>
													<div className="iia-org-info">
														<div className="iia-org-name">{name}</div>
														<div className="iia-org-count">{org_properties.length} PROPERTIES</div>
													</div>
													<div className="iia-org-radio">
														{selectedOrganizationData.id === id ? (
															<CheckCircle2 size={24} color="#0284c7" />
														) : (
															<Circle size={24} color="#cbd5e1" />
														)}
													</div>
												</div>
											);
										})}

										{!isCustomerUser && (
											<div
												className="iia-org-add-card"
												onClick={() => {
													setIsInviteOrgModalOpen(true);
												}}
											>
												<Plus size={20} />
												<span className="iia-org-add-text">Add New Organization</span>
											</div>
										)}
									</div>

									<button
										className={`iia-start-btn iia-org-next-btn ${!selectedOrganizationData.id ? "iia-start-btn-disabled" : ""}`}
										disabled={!selectedOrganizationData.id}
										onClick={() => {
											setFlowStage(FlowStage.INSPECTION_SETUP);
										}}
									>
										Next: Inspection Setup
										<ChevronRight className="iia-start-btn-icon" />
									</button>
								</div>
							</div>
						</div>
					)}
				</div>
			</OllieLayout>

			<Modal
				open={isCreatePropertyModalOpen}
				onCancel={handleAddPropertyCancel}
				footer={null}
				title={null}
				closable={false}
				className="ha-invite-org-modal"
				width={680}
				// Preserve internal form state so the user can re-open and continue editing
				destroyOnClose={false}
			>
				<div className="ha-modal-content">
					<div className="ha-modal-header">
						<span className="ha-modal-title">Add a new property</span>
					</div>
					<PropertyForm
						key={newPropertyFormOutput ? "edit" : "create"}
						initialValues={newPropertyFormOutput ?? undefined}
						submitLabel="Save"
						onSubmit={handleNewPropertySave}
						extraOrganizations={newOrgData ? [{ id: newOrgData.tempId, name: `${newOrgData.name} (New)` }] : []}
						lockedOrganizationId={selectedOrganizationData.id || undefined}
						renderExtraActions={() => (
							<button type="button" className="ha-cancel-btn" onClick={handleAddPropertyCancel}>
								Cancel
							</button>
						)}
					/>
				</div>
			</Modal>

			<InviteOrgModal
				open={isInviteOrgModalOpen}
				orgLabel="organization"
				onCancel={() => setIsInviteOrgModalOpen(false)}
				onSave={handleOnSaveNewOrganization}
			/>

			<LoadingOverlay isLoading={isLoading} tip={""} />
		</>
	);
};

const OllieInspectionAdd = () => {
	const { loggedUserValue } = useLoggedUser();

	if (isOllieInspectionsGatedRole(loggedUserValue.role)) {
		return <OllieInspectionGate />;
	}

	return <OllieInspectionAddForm />;
};

export default OllieInspectionAdd;
